import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../providers/AuthContext';
import Spinner from '../../../../components/Spinner';
import { getWidgetById } from '../../../../services/apiWidgests';
import useWidgetRuntime from '../../../../hooks/useWidgetRuntime';
import { useDashboards } from '../../../../providers/DashboardContext';
import DatabaseOffIco from '../../../../components/icons/DatabaseOffIco';
import { useAppContext } from '../../../../providers/AppContext';

// La unidad no se configura acá: viene de la propia variable ("unity")
// si el usuario la definió; si no la tiene, no se muestra ninguna.
const DEFAULT_STYLE = { color: "#10174c", min: 0, max: 100 };

// dd/mm/yy - HH:MM, igual al formato usado en el resto de la plataforma.
const formatDtm = (dtm) => {
  if (!dtm) return "--";
  const d = new Date(dtm);
  if (isNaN(d.getTime())) return "--";
  const pad = (n) => String(n).padStart(2, "0");
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yy = pad(d.getFullYear() % 100);
  const hh = pad(d.getHours());
  const min = pad(d.getMinutes());
  return `${dd}/${mm}/${yy} - ${hh}:${min}`;
};

// Termómetro minimalista de línea (tubo + bulbo), con marcas de escala y
// las etiquetas de mínimo/máximo. El contorno tubo+bulbo es UN SOLO trazo
// continuo (no dos formas que se superponen): el tubo baja recto hasta el
// punto exacto donde su ancho coincide con el del círculo del bulbo, y ahí
// arranca el arco del bulbo — así no se ve un círculo entero "pegado"
// detrás del tubo, sino una sola silueta.
const ThermometerSvg = ({ value, min, max, color }) => {
  const safeMin = Number(min);
  const safeMax = Number(max);
  const range = safeMax - safeMin || 1;
  const ratio = value === null || value === undefined
    ? 0
    : Math.min(1, Math.max(0, (Number(value) - safeMin) / range));

  const tubeX = 34;
  const tubeWidth = 16;
  const tubeHalf = tubeWidth / 2;
  const tubeTop = 14;
  const bulbCx = tubeX + tubeHalf;
  const bulbCy = 132;
  const bulbR = 17;
  const strokeW = 3;
  // Separación entre el contorno y el mercurio, para que no se vean
  // pegados (mercurio más angosto que el hueco del tubo/bulbo). En el tubo
  // se deja más chica que en el bulbo, porque el tubo es angosto y una
  // separación grande ahí deja muy poco mercurio visible.
  const gapTube = 1.5;
  const gapBulb = 3;

  // Punto donde el lado recto del tubo se convierte en el arco del bulbo:
  // el único punto de la circunferencia cuyo ancho horizontal coincide
  // exactamente con el ancho del tubo, así el trazo recto se prolonga en
  // el arco sin salto ni superposición.
  const neckY = bulbCy - Math.sqrt(bulbR * bulbR - tubeHalf * tubeHalf);
  const tubeBottom = neckY; // borde visible del tubo, para las marcas de escala

  const innerTop = tubeTop + strokeW;
  const fillHeight = (tubeBottom - innerTop) * ratio;
  const fillY = tubeBottom - fillHeight;
  const fillWidth = tubeWidth - strokeW - gapTube * 2;
  const fillX = tubeX + strokeW / 2 + gapTube;
  const bulbFillR = bulbR - strokeW / 2 - gapBulb;

  // El rect de relleno baja hasta el CENTRO del bulbo, bien adentro del
  // círculo, así su borde inferior queda siempre tapado por el círculo de
  // mercurio del bulbo que se dibuja encima.
  const tubeRectBottom = bulbCy;

  const tickCount = 5;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) => {
    const t = i / tickCount;
    return {
      y: tubeTop + (tubeBottom - tubeTop) * t,
      isEdge: i === 0 || i === tickCount,
      label: i === 0 ? Math.round(safeMax) : i === tickCount ? Math.round(safeMin) : null,
    };
  });

  // Contorno único: semicírculo arriba, bajada recta por ambos lados hasta
  // el "cuello" (neckY), y un solo arco grande que envuelve el bulbo por
  // abajo para volver al lado opuesto.
  const outlinePath = `
    M ${tubeX} ${tubeTop + tubeHalf}
    A ${tubeHalf} ${tubeHalf} 0 1 1 ${tubeX + tubeWidth} ${tubeTop + tubeHalf}
    L ${tubeX + tubeWidth} ${neckY}
    A ${bulbR} ${bulbR} 0 1 1 ${tubeX} ${neckY}
    Z
  `;

  return (
    <svg viewBox="0 0 100 160" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
      {/* 1) relleno (mercurio) proporcional al valor, más angosto que el
          hueco del tubo para que quede separado del contorno */}
      {fillHeight > 0 && (
        <rect
          x={fillX} y={fillY}
          width={fillWidth} height={fillHeight + (tubeRectBottom - tubeBottom)}
          rx={fillWidth / 2}
          fill={color}
        />
      )}
      {/* 2) bulbo: círculo sólido, tapa el borde inferior del rect de
          relleno, separado del contorno por su propio gap */}
      <circle cx={bulbCx} cy={bulbCy} r={bulbFillR} fill={color} />
      {/* 3) contorno único (tubo + bulbo en un solo trazo), por encima de
          todo, prolijo */}
      <path d={outlinePath} fill="none" stroke={color} strokeWidth={strokeW} strokeLinejoin="round" />

      {/* Marcas de escala */}
      {ticks.map((tick, i) => (
        <g key={i}>
          <line
            x1={tubeX + tubeWidth + 4} y1={tick.y}
            x2={tubeX + tubeWidth + (tick.isEdge ? 12 : 9)} y2={tick.y}
            stroke={color} strokeWidth={tick.isEdge ? 2 : 1.5}
          />
          {tick.label !== null && (
            <text
              x={tubeX + tubeWidth + 15} y={tick.y + 4}
              fontSize="12" fontWeight="600" fill={color}
            >
              {tick.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

const TelemetryThermometerWidget = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { dashboard, selectedDatarange } = useDashboards();
  const { data: runtimeData, error: runtimeError, loading: fetchingWidgetData } = useWidgetRuntime(id, dashboard?._id);
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [nDecimals, setNDecimals] = useState(1);
  const { addToast } = useAppContext();

  const value = runtimeData?.value ?? null;
  const dtm = runtimeData?.dtm ?? null;
  const unity = runtimeData?.unity || "";

  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setStyle({ ...DEFAULT_STYLE, ...(response?.configuration?.style || {}) });
      setNDecimals(response?.configuration?.nDecimals ?? 1);

      setFetchingWidgetInfo(false);
    } catch (error) {
      if (!error || !error.response) {
        addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
        setFetchingWidgetInfo(false);
        return;
      }
      if (error.response?.status === 404) {
        console.log("Widget no encontrado");
      } else if (error.response?.status === 500) {
        console.log("Error en el servidor, intente nuevamente");
      } else {
        navigate('/logout');
      }
      setFetchingWidgetInfo(false);
    }
  };

  useEffect(() => {
    if (runtimeError) {
      addToast("No se pudo completar la consulta. Inténtalo nuevamente", 2);
    }
  }, [runtimeError]);

  useEffect(() => {
    refreshWidgetInfo();
  }, [selectedDatarange]);

  return (
    <div
      className="w-full h-full p-3 bg-white border border-gray-300 rounded overflow-hidden"
      style={{ containerType: 'size' }}
    >
      {(fetchingWidgetData || fetchingWidgetInfo) ? (
        <div className='w-full h-full flex justify-center items-center'>
          <Spinner />
        </div>
      ) : value === null ? (
        <div className='w-full h-full flex flex-col items-center justify-center'>
          <span className='text-lg text-gray-700'>No hay datos por el momento</span>
          <DatabaseOffIco width='40px' height='40px' fill='gray' />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center overflow-hidden" style={{ gap: '4cqw' }}>
          <div className="h-full shrink-0" style={{ width: '38cqw', maxWidth: '45%' }}>
            <ThermometerSvg value={value} min={style.min} max={style.max} color={style.color} />
          </div>
          <div className="flex flex-col items-start min-w-0 overflow-hidden">
            <span
              className="font-bold truncate w-full"
              style={{ color: style.color, fontSize: 'clamp(0.9rem, 9cqw, 2.5rem)' }}
            >
              {Number(value).toFixed(nDecimals ?? 1)}{unity ? ` ${unity}` : ""}
            </span>
            <span
              className="text-gray-500 font-semibold truncate w-full"
              style={{ fontSize: 'clamp(0.55rem, 3.2cqw, 0.95rem)' }}
            >
              Última actualización:
            </span>
            <span
              className="text-gray-500 truncate w-full"
              style={{ fontSize: 'clamp(0.55rem, 3.2cqw, 0.95rem)' }}
            >
              {formatDtm(dtm)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelemetryThermometerWidget;
